// @flow
import * as React from "react";
import {
  Typography,
  Popover,
  Box,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import { FilterRow, joins, operators } from "../FilterRow";
import { caseContext, defaultColumns, getColId } from "../../organisms/workItemsTable";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import add from "../../../../public/assets/icons/add.svg";
import theme from "../../../theme/Theme";
import { WidgetConstants } from "../../../utils/common";
import { StrictModeDroppable } from "../StrictModeDroppable";
type Props = {
  anchorEl: any;
  handleClose: any;
  filters: any;
  setFilters: any;
  applyFilters: any;
  removeFilters: any;
  client: any;
  allCoders?: any[];
  allGroups?: any[];
};

export const CaseFilters = ({
  anchorEl,
  handleClose,
  filters,
  setFilters,
  applyFilters,
  removeFilters,
  client,
  allCoders,
  allGroups,
  ...props
}: Props) => {
  const { columnOptions, directoryType } = React.useContext(caseContext);
  const [uiFilters, setUiFilters] = React.useState([]);
  const [clearFirstFilter, setClearFirstFilter] = React.useState(false);

  //region Functions
  const onFilterModified = (filter: any, apply?: boolean) => {
    const l = [];
    let flag = false;
    filters?.map((obj) => {
      if (obj?.filterIndex !== filter?.filterIndex) {
        l.push(obj);
      } else {
        flag = true;
        l.push(filter);
      }
    });
    if (!flag) {
      l.push(filter);
    }
    setFilters(l);
    if (apply) {
      applyFilters(l);
    }
  };
  const onFilterDelete = (uiFilter) => {
    if (uiFilter == 0) {
      if (uiFilters?.length === 1) {
        setUiFilters([]);
        removeFilters();
      } else {
        let nextFilterIndex = uiFilters?.[1];
        const updatedUiFilters = uiFilters.slice(1);
        updatedUiFilters[0] = 0;
        setUiFilters(updatedUiFilters);
        const updatedFilters = [];
        filters?.map((filter) => {
          if (filter.filterIndex !== 0) {
            if (filter?.filterIndex !== nextFilterIndex) {
              updatedFilters.push(filter);
            } else {
              updatedFilters.push({
                ...filter,
                filterIndex: 0,
                join: undefined,
              });
            }
          }
        });
        setFilters(updatedFilters);
        applyFilters(updatedFilters);
      }
    } else {
      setUiFilters(uiFilters?.filter((filter) => filter != uiFilter));
      const newFilters = filters?.filter(
        (filter) => filter.filterIndex != uiFilter
      );
      setFilters(newFilters);
      newFilters?.length ? applyFilters(newFilters) : removeFilters();
    }
  };
  const onAddFilter = () => {
    let newUiFilterPosition;
    if (uiFilters?.length) {
      newUiFilterPosition = uiFilters[uiFilters?.length - 1] + 1;
    } else {
      newUiFilterPosition = 0;
    }
    let newFilters;
    if (uiFilters?.length) {
      newFilters = [...uiFilters, uiFilters[uiFilters?.length - 1] + 1];
    } else {
      newFilters = [0];
    }
    
    const newOptions = columnOptions?.filter((column) => defaultColumns.indexOf(getColId(column)) === -1);
    
    onFilterModified({
      filterIndex: newUiFilterPosition,
      fieldName: newOptions?.[0]?.field,
      fieldId: newOptions?.[0].context.id,
      operator: operators[0]?.value,
      value: null,
      join: uiFilters?.length !== 0 ? joins[0]?.value : undefined,
    });
    setUiFilters(newFilters);
  };
  const handleDrop = (droppedItem) => {
    if (!droppedItem.destination) return;
    var updatedList = [...uiFilters];
    const destinationIndex = droppedItem.destination.index;
    const sourceIndex = droppedItem.source.index;
    const [reorderedItem] = updatedList.splice(sourceIndex, 1);
    updatedList.splice(destinationIndex, 0, reorderedItem);
    setUiFilters(updatedList);

    let draggedItemContent;
    let draggedOverItemContent;
    for (let filter of filters) {
      if (filter?.filterIndex === destinationIndex) {
        draggedOverItemContent = filter;
      } else if (filter?.filterIndex === sourceIndex) {
        draggedItemContent = filter;
      }
      if (draggedItemContent && draggedOverItemContent) {
        break;
      }
    }
    const l = [];
    filters?.map((filter) => {
      if (filter?.filterIndex === destinationIndex) {
        l.push({
          ...draggedItemContent,
          filterIndex: filter?.filterIndex,
          join: filter?.join,
        });
      } else if (filter?.filterIndex === sourceIndex) {
        l.push({
          ...draggedOverItemContent,
          filterIndex: filter?.filterIndex,
          join: filter?.join,
        });
      } else {
        l.push(filter);
      }
    });
    setFilters(l);
    applyFilters(l);
  };
  //endregion

  //region Lifecycle
  React.useEffect(() => {
    const firstFilter = filters?.filter((temp) => temp.filterIndex === 0);
    if (firstFilter && firstFilter.length !== 0)
      setUiFilters(filters?.map((filter) => filter.filterIndex));
    else setUiFilters([...filters?.map((filter) => filter.filterIndex)]);
  }, [filters]);
  //endregion

  return (
    <Popover
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <Box
        sx={{
          minHeight: "50px",
          margin: "20px",
          height: "fit-content",
          width: "fit-content",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ mb: "10px" }}>
            Filters
          </Typography>
          <Divider
            sx={{
              bgcolor: theme.palette.artisticStroke100.main,
              mb: "20px",
            }}
          />
        </Box>
        <DragDropContext onDragEnd={handleDrop}>
          <Box sx={{ marginBottom: "10px" }}>
            {uiFilters?.length ? (
              <>
                <StrictModeDroppable droppableId="list-container">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {uiFilters.map((uiFilter, index) => {
                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              margin: "10px 30px 10px 0px",
                            }}
                          >
                            <Draggable
                              key={index}
                              draggableId={uiFilter.toString()}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}
                                >
                                  <FilterRow
                                    client={client}
                                    filterIndex={uiFilter}
                                    resetAll={
                                      uiFilter == 0
                                        ? clearFirstFilter
                                        : undefined
                                    }
                                    setResetAll={setClearFirstFilter}
                                    onFilterModified={onFilterModified}
                                    currentFilter={
                                      filters?.filter(
                                        (filter) =>
                                          filter.filterIndex === uiFilter
                                      )?.[0]
                                    }
                                    onFilterDelete={onFilterDelete}
                                    allCoders={allCoders}
                                    allGroups={allGroups}
                                    worklist={directoryType === WidgetConstants.WORKITEM}
                                    key={uiFilter.toString()}
                                  />
                                </div>
                              )}
                            </Draggable>
                          </Box>
                        );
                      })}
                    </div>
                  )}
                </StrictModeDroppable>
              </>
            ) : (
              <Typography sx={{ color: "#abafb0" }}>
                No filter conditions are applied
              </Typography>
            )}
          </Box>
        </DragDropContext>

        <Box sx={{ display: "flex", gap: "5px" }}>
          <Button
            onClick={onAddFilter}
            disableRipple={true}
            sx={{ textTransform: "none", paddingLeft: 0, ml: "-10px" }}
          >
            <IconButton disableRipple={true}>
              <img src={add} alt="add" style={{ height: "14px" }} />
            </IconButton>{" "}
            Add Condition
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
